import { useCallback } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import { ExpansionButtonCell } from "app/components/React/Table/cells/ExpansionButtonCell";

import type {
    ILanguageLocaleAndDescription,
    IOpenSERPParams,
} from "../../../reports/shared/Table/TableUtils/RankTrackerTablesTypes";
import { useSerpOverlayContext } from "../SerpOverlay";

import { LocationIcon } from "./LocationIcon";
import { ScrapeInProgressBadge } from "./ScrapeInProgressBadge";
import { KeywordCellRoot, KeywordTextCell, KeywordWithBadgeWrapper } from "./styles";

type KeywordCellProps = {
    keyword: string;
    geoLocation: ILanguageLocaleAndDescription;
    isSingleLocation: boolean;
    isSerpButtonDisabled: boolean;
    serpLinkProps: IOpenSERPParams;
    showScrapeInProgressBadge?: boolean;
};

/** Requires SerpOverlayProvider — mounted in KeywordsCampaignsModuleFrame. */
export const KeywordCell = ({
    keyword,
    geoLocation,
    isSingleLocation,
    isSerpButtonDisabled,
    serpLinkProps,
    showScrapeInProgressBadge = false,
}: KeywordCellProps) => {
    const { translate } = useTranslate();
    const { openSerpOverlay } = useSerpOverlayContext();
    const WrappedComponent = useCallback(() => {
        if (!showScrapeInProgressBadge) {
            return <KeywordTextCell>{keyword}</KeywordTextCell>;
        }
        return (
            <KeywordWithBadgeWrapper>
                <KeywordTextCell $withBadge>{keyword}</KeywordTextCell>
                <ScrapeInProgressBadge />
            </KeywordWithBadgeWrapper>
        );
    }, [keyword, showScrapeInProgressBadge]);

    return (
        <KeywordCellRoot>
            <ExpansionButtonCell
                row={null}
                buttonText={translate("rankTracker.keywordsReport.table.column.keyword.button")}
                isDisabled={isSerpButtonDisabled}
                onButtonClick={() => openSerpOverlay(serpLinkProps)}
                wrappedComponentTooltipText={translate(
                    isSerpButtonDisabled
                        ? "rankTracker.keywordsReport.table.column.keyword.serpSnapshot.disabled.tooltip"
                        : "rankTracker.keywordsReport.table.column.keyword.serpSnapshot.tooltip",
                )}
                WrappedComponent={WrappedComponent}
                presentJustOnHover={false}
                beforeButton={!isSingleLocation ? <LocationIcon geoLocation={geoLocation} /> : undefined}
            />
        </KeywordCellRoot>
    );
};
